
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  TextField,
  InputAdornment,
  TablePagination,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EditModal from './EditModal';
import { useNavigate } from 'react-router-dom';
import EntryForm from './EntryForm';

const DataTable = ({ title, onHardwareSelect, selectedHardware, takenBy, onTakenByChange, initialFilter }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openSelectedSnackbar, setOpenSelectedSnackbar] = useState(false);
  

  const fetchData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      let result = await response.json();

      // Apply initial filter if present
      if (initialFilter) {
        if (initialFilter.filter === 'paymentStatus') {
          result = result.filter(row => row['Payment Status'] === initialFilter.value);
        } else if (initialFilter.filter === 'date') {
          result = result.filter(row => row.Date === initialFilter.value);
        }
        // 'all' filter means no specific filter, just show all
      }

      setData(result);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [initialFilter]); // Re-fetch data when initialFilter changes

  const handleEditModalOpen = (row) => {
    setEditData(row);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditData(null);
    fetchData(); // Reload data after edit
  };

  const navigate = useNavigate();

  const handleEntryFormOpen = () => {
    navigate('/systems/new');
  };

  const handleSelected = (row) => {
    if (typeof onHardwareSelect === 'function') {
      onHardwareSelect(row);
      setOpenSelectedSnackbar(true);
    }
  };

  

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries/${id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          fetchData(); // Reload data after delete
          setOpenSnackbar(true);
        } else {
          console.error('Failed to delete entry');
        }
      } catch (error) {
        console.error('Error deleting entry:', error);
      }
    }
  };

  

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset page when searching
  };

  const filteredData = data.filter((row) =>
    Object.values(row).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Loading {title}...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading {title}
        </Typography>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Entry deleted successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={openSelectedSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSelectedSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSelectedSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Item selected successfully!
        </Alert>
      </Snackbar>
      <Typography variant="h4" gutterBottom>
        {title || 'Customer Details'}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {title === 'Hardware List' ? null : (
          <Button variant="contained" color="primary" onClick={handleEntryFormOpen}>
            Add New Entry
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {(rowsPerPage > 0
          ? filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : filteredData
        ).map((row) => (
          <Grid item xs={12} key={`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`}>
            <Card sx={{ 
              boxShadow: 3, 
              transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' } 
            }}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Typography variant="h6" gutterBottom>
                      {row['Customer Name']}
                    </Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Place:</strong> {row.Place}</Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Date:</strong> {row.Date}</Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Time:</strong> {row.Time}</Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Serial No:</strong> {row['Serial No']}</Typography>
                    <Typography variant="body2" color="text.secondary"><strong>System Name:</strong> {row['System Name']}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary"><strong>Contact Number:</strong> {row['Contact Number'] || 'N/A'}</Typography>
                      {row['Contact Number'] && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => window.open(`https://wa.me/${row['Contact Number']}`, '_blank')}
                            sx={{ ml: 1 }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(`tel:${row['Contact Number']}`, '_blank')}
                            sx={{ ml: 0.5 }}
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary"><strong>Customer ID:</strong> {row['Customer ID']}</Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Address:</strong> {row.Address}</Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Customer Issue:</strong> {row['Customer Issue']}</Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Typography variant="body2" color={row['Payment Status'] === 'Paid' ? 'success.main' : 'error.main'}>
                      {row['Payment Status']}
                    </Typography>

                    <CardActions>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <Button fullWidth variant="contained" color="primary" onClick={() => handleEditModalOpen(row)} sx={{ mr: 1 }}>Edit</Button>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button fullWidth variant="contained" color={selectedHardware.some(item => item.id === row.id) ? "success" : "primary"} onClick={() => handleSelected(row)} disabled={!takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] || takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === ''}>Selected</Button>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button fullWidth variant="contained" color="error" onClick={() => handleDelete(row.id)} sx={{ mr: 1 }}>Delete</Button>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth sx={{ minWidth: 120 }}>
                            <InputLabel>Taken by</InputLabel>
                            <Select
                              value={takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] || ''}
                              onChange={(e) => {
                                onTakenByChange(e, row);
                                onHardwareSelect(row);
                              }}
                              label="Taken by"
                              size="medium"
                            >
                              <MenuItem value="" disabled>Select Name</MenuItem>
                              <MenuItem value="Kishore S" sx={{ fontWeight: takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === 'Kishore S' ? 'bold' : 'normal' }}>Kishore S</MenuItem>
                              <MenuItem value="kishore Kumar S" sx={{ fontWeight: takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === 'kishore Kumar S' ? 'bold' : 'normal' }}>kishore Kumar S</MenuItem>
                              <MenuItem value="Meganathan J" sx={{ fontWeight: takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === 'Meganathan J' ? 'bold' : 'normal' }}>Meganathan J</MenuItem>
                              <MenuItem value="Mohammed Rehman M" sx={{ fontWeight: takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === 'Mohammed Rehman M' ? 'bold' : 'normal' }}>Mohammed Rehman M</MenuItem>
                              <MenuItem value="Indumathi S" sx={{ fontWeight: takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === 'Indumathi S' ? 'bold' : 'normal' }}>Indumathi S</MenuItem>
                              <MenuItem value="Gokul S" sx={{ fontWeight: takenBy[`${row['Customer Name']}-${row['Serial No']}-${row['Date']}`] === 'Gokul S' ? 'bold' : 'normal' }}>Gokul S</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </CardActions>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      

      <EditModal
        open={editModalOpen}
        handleClose={handleEditModalClose}
        data={editData}
        onSave={async (updatedData) => {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/entries/${updatedData.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updatedData),
            });
            const result = await response.json();
            console.log(result);
            handleEditModalClose();
          } catch (error) {
            console.error('Error updating data:', error);
          }
        }}
      />
      
    </Box>
  );
};

export default DataTable;
